import { ArrowRight, Check, Share2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Reveal } from '@/components/Reveal';
import { TextReveal } from '@/components/TextReveal';
import { MagneticButton } from '@/components/MagneticButton';
import { SITE_IMAGES } from '@/lib/siteImages';
import { BLUEPRINT_PAGE } from '@/constants/testIds';

/** Full-bleed hero: title, tagline, mood keywords, book + share actions. */
export const BlueprintHero = ({ content: c, firstName, reviewed, bookingUrl, onCopyLink }) => (
  <section className="relative min-h-[70vh] flex items-end lxra-grain overflow-hidden">
    <img src={SITE_IMAGES.servicesHero} alt="" className="absolute inset-0 h-full w-full object-cover" loading="eager" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-[#0A0A0A]/40" />
    <div className="relative z-10 mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-10 pb-14 pt-36">
      <Reveal>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <p className="text-xs sm:text-sm tracking-[0.22em] uppercase text-[hsl(var(--lxra-gold))]">
            Creative Blueprint {firstName && `· for ${firstName}`}
          </p>
          {reviewed && (
            <Badge className="bg-[hsl(var(--lxra-gold))]/15 text-[hsl(var(--lxra-gold-hover))] border border-[hsl(var(--lxra-gold))]/30 hover:bg-[hsl(var(--lxra-gold))]/15" data-testid="blueprint-reviewed-badge">
              <Check className="h-3 w-3 mr-1" /> Reviewed by your photographer
            </Badge>
          )}
        </div>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-[0.04em] leading-[0.95] text-[hsl(var(--lxra-fg))] max-w-[18ch]" data-testid={BLUEPRINT_PAGE.title}>
          <TextReveal text={c.title.toUpperCase()} />
        </h1>
        <div className="mt-4 h-[2px] w-16 bg-[hsl(var(--lxra-gold))]" />
        {c.tagline && (
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-[hsl(var(--lxra-muted))] leading-relaxed" data-testid={BLUEPRINT_PAGE.tagline}>
            {c.tagline}
          </p>
        )}
        <div className="mt-6 flex flex-wrap gap-2">
          {(c.mood_keywords || []).map((k) => (
            <span key={k} className="rounded-full border border-[hsl(var(--lxra-border))] px-3 py-1 text-[11px] uppercase tracking-[0.12em] text-[hsl(var(--lxra-fg))]">{k}</span>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <MagneticButton>
            <Link to={bookingUrl} data-testid={BLUEPRINT_PAGE.bookNow}>
              <button className="inline-flex items-center h-12 px-7 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] transition-colors duration-200 text-xs uppercase tracking-[0.1em] font-semibold">
                Book This Session
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>
            </Link>
          </MagneticButton>
          <button
            type="button"
            onClick={onCopyLink}
            data-testid={BLUEPRINT_PAGE.copyLink}
            className="inline-flex items-center h-12 px-6 rounded-xl border border-[hsl(var(--lxra-gold))]/50 text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))] transition-colors duration-200 text-xs uppercase tracking-[0.1em] font-medium"
          >
            <Share2 className="mr-2 h-4 w-4" />
            Share Link
          </button>
        </div>
      </Reveal>
    </div>
  </section>
);
